import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { decodeJWT, getTenantFromToken, isTokenValid, readDcAuthFromStorage } from '../lib/auth';
import { firebaseLogout } from '../lib/firebase';
import { getDefaultTenantId, isProductionHost, sanitizeStoredAuthForHost } from '../lib/cpClient';
import type { JWTPayload } from '../lib/schemas';

interface AuthState {
  token: string | null;
  payload: JWTPayload | null;
  selectedTenant: string;
  persistToken: boolean;
  isAuthenticated: boolean;
  hydrated: boolean;

  signIn: (token: string, persist: boolean) => void;
  signOut: () => void;
  setTenant: (tenant: string) => void;
  switchToTokenTenant: () => void;
  checkPersistedAuth: () => void;
  setHydrated: (hydrated: boolean) => void;
}

const fallbackTenant = () => getDefaultTenantId() || import.meta.env.VITE_TENANT_DEFAULT || 'dev';

function clearLegacyAuthStorage() {
  localStorage.removeItem('dc.auth');
  localStorage.removeItem('dc.persist');
}

function clearAuthState(set: (partial: Partial<AuthState>) => void) {
  set({
    token: null,
    payload: null,
    selectedTenant: fallbackTenant(),
    isAuthenticated: false,
    persistToken: false,
    hydrated: true,
  });
  clearLegacyAuthStorage();
}

function assertProductionTenant(tokenTenant: string | null) {
  if (!isProductionHost()) return;

  const hostedTenant = getDefaultTenantId();
  if (!tokenTenant) {
    throw new Error('Missing tenant claim for production session');
  }
  if (hostedTenant && tokenTenant !== hostedTenant) {
    throw new Error(`Hosted tenant mismatch for production session: ${tokenTenant}`);
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      payload: null,
      selectedTenant: fallbackTenant(),
      persistToken: false,
      isAuthenticated: false,
      hydrated: false,

      signIn: (token: string, persist: boolean) => {
        if (!isTokenValid(token)) {
          throw new Error('Invalid or expired token');
        }

        const payload = decodeJWT(token);
        if (!payload) {
          throw new Error('Failed to decode token');
        }

        const tokenTenant = getTenantFromToken(token);
        assertProductionTenant(tokenTenant);
        const selectedTenant = tokenTenant || getDefaultTenantId() || get().selectedTenant || fallbackTenant();

        set({
          token,
          payload,
          persistToken: persist,
          isAuthenticated: true,
          selectedTenant,
          hydrated: true,
        });

        localStorage.setItem('dc.auth', JSON.stringify({ token, tenant: selectedTenant }));
        if (persist) {
          localStorage.setItem('dc.persist', '1');
        } else {
          localStorage.removeItem('dc.persist');
        }
      },

      signOut: () => {
        set({
          token: null,
          payload: null,
          selectedTenant: fallbackTenant(),
          isAuthenticated: false,
          persistToken: false,
          hydrated: true,
        });

        clearLegacyAuthStorage();
        void firebaseLogout();
      },

      setTenant: (tenant: string) => {
        const hostedTenant = getDefaultTenantId();
        const nextTenant = isProductionHost() && hostedTenant ? hostedTenant : tenant || fallbackTenant();
        set({ selectedTenant: nextTenant });
      },

      switchToTokenTenant: () => {
        const { token } = get();
        if (!token) return;
        const tokenTenant = getTenantFromToken(token);
        if (tokenTenant) {
          set({ selectedTenant: tokenTenant });
        }
      },

      checkPersistedAuth: () => {
        sanitizeStoredAuthForHost();

        const { token, persistToken } = get();
        const legacyAuth = readDcAuthFromStorage();
        const legacyPersist = localStorage.getItem('dc.persist') === '1';

        const tokenToCheck = token || legacyAuth.token;
        const shouldPersist = persistToken || legacyPersist;

        if (tokenToCheck && shouldPersist) {
          if (isTokenValid(tokenToCheck)) {
            const payload = decodeJWT(tokenToCheck);
            const tokenTenant = getTenantFromToken(tokenToCheck);
            const hostedTenant = getDefaultTenantId();

            if (isProductionHost() && (!tokenTenant || (hostedTenant && tokenTenant !== hostedTenant))) {
              clearAuthState(set);
              return;
            }

            const selectedTenant = tokenTenant || legacyAuth.tenant || hostedTenant || get().selectedTenant || fallbackTenant();

            set({
              token: tokenToCheck,
              payload,
              isAuthenticated: true,
              persistToken: shouldPersist,
              selectedTenant,
              hydrated: true,
            });

            localStorage.setItem('dc.auth', JSON.stringify({ token: tokenToCheck, tenant: selectedTenant }));
            if (shouldPersist) {
              localStorage.setItem('dc.persist', '1');
            }
          } else {
            clearAuthState(set);
          }
        } else {
          set({ hydrated: true, selectedTenant: get().selectedTenant || fallbackTenant() });
        }
      },

      setHydrated: (hydrated: boolean) => {
        set({ hydrated });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) =>
        state.persistToken
          ? { token: state.token, persistToken: state.persistToken, selectedTenant: state.selectedTenant }
          : { persistToken: false },
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        try {
          state.checkPersistedAuth();
        } catch {
          state.setHydrated(true);
        }
      },
    }
  )
);
